import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import { robotsApi } from "api";
import { useLLM, useSnackbar, useTranslations } from "hooks";
import useKonamiCode from "hooks/useKonamiCode";
import { Section, Field } from "model/robot";
import SectionEditor from "components/RobotFormBuilder/SectionEditor";
import JsonPreview from "components/RobotFormBuilder/JsonPreview";
import AiDrawer from "components/aiChat/AiDrawer";
import SaveIcon from '@mui/icons-material/Save';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { getTranslation } from "common";
interface FormBuilderPageProps {
  robotId?: number;
}

export default function FormBuilderPage({ robotId }: FormBuilderPageProps) {
  const { id } = useParams<{ id: string }>();
  const effectiveId = robotId ?? Number(id);
  const translate = useTranslations()
  const [sections, setSections] = useState<Section[]>([
    { name: getTranslation(translate, "pm.propertyeditor.default"), fields: [] },
  ]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"form" | "json">("form");
  const { sendSnack } = useSnackbar();
  const { pathname } = useLocation();
  const [robotName, setRobotName] = useState<string>("");

  const {
    aiAnswers,
    handleSubmit,
    loading: llmLoading,
    llmInputRef,
    modelInputRef,
    openAI,
    setOpenAI,
  } = useLLM("robot/request", true, false);

  const handleSubmitLLM = async () => {
    await handleSubmit({ request_type: "robot_schema", robotId: effectiveId });
  };

  useEffect(() => {
    setOpenAI(false);
  }, [pathname, setOpenAI]);

  useEffect(() => {
    if (!effectiveId) return;
    (async () => {
      try {
        const { data } = await robotsApi.get(`/${effectiveId}`);
        if (data.name) {
          setRobotName(data.name);
        }
        if (data.orgjsonschema) {
          const parsed = JSON.parse(data.orgjsonschema);

          const formSections: Section[] = Object.entries(parsed).map(
            ([sectionName, sectionDef]: any) => ({
              name: sectionName,
              fields: Object.entries(sectionDef.properties || {}).map(
                ([fieldName, fieldDef]: any) => ({
                  name: fieldName,
                  type:
                    fieldDef.format === "date"
                      ? "date"
                      : fieldDef.format === "textarea"
                        ? "textarea"
                        : fieldDef.contentEncoding === "base64"
                          ? "file"
                          : fieldDef.enum
                            ? "dropdown"
                            : fieldDef.type,
                  required: fieldDef.required,
                  values: fieldDef.enum
                    ? fieldDef.enum.map((e: string, i: number) => ({
                      key: e,
                      value: fieldDef["x-enum-varnames"]?.[i] || e,
                    }))
                    : undefined,
                  minimum: fieldDef.minimum,
                  maximum: fieldDef.maximum,
                  // isRange: fieldDef.isRange,
                })
              ),
            })
          );
          if (formSections.length > 0) {
            setSections(formSections);
          }
        }
      } catch {
        sendSnack({
          message: `${getTranslation(translate, "schema.loading_error")}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId, sendSnack]);

  const addSection = () =>
    setSections([
      ...sections,
      { name: `${getTranslation(translate, "generic.new_section")} ${" "}${sections.length + 1}`, fields: [] },
    ]);

  const updateSectionName = (sectionIndex: number, name: string) =>
    setSections(
      sections.map((s, i) => (i === sectionIndex ? { ...s, name } : s))
    );

  const removeSection = (sectionIndex: number) =>
    setSections(sections.filter((_, i) => i !== sectionIndex));

  const addField = (sectionIndex: number) => {
    const newSections = [...sections];
    const newField: Field = {
      name: `${getTranslation(translate, "generic.field")}${newSections[sectionIndex].fields.length + 1}`,
      type: "string",
    };
    newSections[sectionIndex].fields.push(newField);
    setSections(newSections);
  };

  const updateField = (
    sectionIndex: number,
    fieldIndex: number,
    changes: Partial<Field>
  ) => {
    const newSections = [...sections];
    newSections[sectionIndex].fields[fieldIndex] = {
      ...newSections[sectionIndex].fields[fieldIndex],
      ...changes,
    };
    setSections(newSections);
  };

  const removeField = (sectionIndex: number, fieldIndex: number) => {
    const newSections = [...sections];
    newSections[sectionIndex].fields.splice(fieldIndex, 1);
    setSections(newSections);
  };

  const buildFieldDefinition = (field: Field) => {
    let def: any = {};

    switch (field.type) {
      case "textarea":
        def = { type: "string", format: "textarea" };
        break;
      case "date":
        def = { type: "string", format: "date" };
        break;
      case "file":
        def = {
          type: "string",
          contentEncoding: "base64",
          contentMediaType: "application/octet-stream",
        };
        break;
      case "dropdown":
        def = {
          type: "string",
          enum: field.values?.map((v) => v.key),
          "x-enum-varnames": field.values?.map((v) => v.value),
        };
        break;
      default:
        def = { type: field.type };
    }

    if (field.enum) def.enum = field.enum;
    if (field.minimum !== undefined) def.minimum = field.minimum;
    if (field.maximum !== undefined) def.maximum = field.maximum;
    // if (field.isRange !== undefined) def.isRange = field.isRange;
    def.required = !!field.required;

    return def;
  };

  const buildSchema = () => {
    const schema: any = {};
    sections.forEach((section) => {
      if (!section.name) return;
      if (!section.fields || section.fields.length === 0) return;

      const sectionProps: any = {
        properties: {},
      };

      section.fields.forEach((f) => {
        if (!f.name) return;
        sectionProps.properties[f.name] = buildFieldDefinition(f);
      });

      schema[section.name] = sectionProps;
    });
    return schema;
  };

  const handleSave = async () => {
    if (!effectiveId) return;

    const trimmedSections = sections.map(section => ({
      ...section,
      name: section.name.trim(),
      fields: section.fields.map(field => ({
        ...field,
        name: field.name.trim(),
      })),
    }));

    for (const section of trimmedSections) {
      if (!section.name || section.name === "") {
        sendSnack({
          message: getTranslation(translate, "section.name_empty_error"),
          type: "error",
        });
        return;
      }
    }

    for (const section of trimmedSections) {
      for (const field of section.fields) {
        if (!field.name || field.name === "") {
          sendSnack({
            message: getTranslation(translate, "field.name_empty_error"),
            type: "error",
          });
          return;
        }
      }
    }

    setSections(trimmedSections);

    const schema: any = {};
    trimmedSections.forEach((section) => {
      if (!section.name) return;
      if (!section.fields || section.fields.length === 0) return;

      const sectionProps: any = {
        properties: {},
      };

      section.fields.forEach((f) => {
        if (!f.name) return;
        sectionProps.properties[f.name] = buildFieldDefinition(f);
      });

      schema[section.name] = sectionProps;
    });

    const schemaString = JSON.stringify(schema, null, 2);
    try {
      const { data } = await robotsApi.put(`/${effectiveId}`, {
        orgjsonschema: schemaString,
        actuischema: "{}",
      });
      sendSnack({
        message: data.responsemessage,
        type: "success",
      });
    } catch { }
  };

  useKonamiCode(
    ["f", "l", "o", "w", "e", "n", "t", "i", "2"],
    () => {
      setOpenAI(true);
    },
    { caseInsensitive: true, matchMode: "key" }
  );

  if (!effectiveId) return <Typography>{getTranslation(translate, "invalid.robot.id")}</Typography>;
  if (loading) return <Typography><CircularProgress /></Typography>;

  return (
    <>
      <AiDrawer
        variant="persistent"
        modelInputRef={modelInputRef}
        llmInputRef={llmInputRef}
        LLMLoading={llmLoading}
        aiAnswers={aiAnswers}
        handleSubmitLLM={handleSubmitLLM}
        openAI={openAI}
        setOpenAI={setOpenAI}
        isDashboard={false}
      />
      <Box
        p={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '800px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5">
              {getTranslation(translate, "robot.property_builder")} - {robotName}
            </Typography>
            <Box>
              {/* <IconButton onClick={() => setOpenAI(true)}>
                <AutoAwesome />
              </IconButton> */}
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_, newView) => newView && setView(newView)}
                size="small"
              >
                <ToggleButton value="form">{getTranslation(translate, "robot.property_builder")}</ToggleButton>
                <ToggleButton value="json">JSON Schema</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {view === "form" ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {sections.map((section, sectionIndex) => (
                  <SectionEditor
                    key={sectionIndex}
                    sectionIndex={sectionIndex}
                    section={section}
                    isOnlySection={sections.length <= 1}
                    updateSectionName={updateSectionName}
                    removeSection={removeSection}
                    addField={addField}
                    updateField={updateField}
                    removeField={removeField}
                  />
                ))}
              </Box>

              <Box mt={2} sx={{ display: 'flex', gap: 2, marginLeft: 'auto', marginRight: 'auto' }}>
                <Tooltip title={getTranslation(translate, "save.schema")}>
                  <IconButton onClick={handleSave}>
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={getTranslation(translate, "generic.new_section")}>
                  <IconButton onClick={addSection} >
                    <PlaylistAddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <JsonPreview schema={buildSchema()} />
          )}
        </Box>
      </Box>
    </>
  );
}